import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const TEXTURE_URL =
  "https://images.unsplash.com/photo-1533628635777-112b2239b1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwbGFzdGVyJTIwd2FsbCUyMHRleHR1cmV8ZW58MHx8fHwxNzg2ODE5NzA3fDA&ixlib=rb-4.1.0&q=85";

const ALTBAU_URL =
  "https://images.unsplash.com/photo-1784294460266-ff0690c51861?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxpbnRlcmlvciUyMHJvb20lMjBiZWZvcmUlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4NjgxOTcwN3ww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const content = useContent();
  const { hero, contact } = content;
  const whatsappUrl = `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(contact.whatsapp_text)}`;
  const lines = [hero.line1, hero.line2, hero.line3];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textureY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const altbauY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const wordY = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);

  return (
    <section
      ref={ref}
      id="start"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col overflow-hidden pt-16"
    >
      <motion.div
        style={{ y: altbauY }}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
      >
        <img
          src={hero.bg_url || ALTBAU_URL}
          alt=""
          className="h-full w-full object-cover"
          style={{ opacity: hero.bg_opacity ?? 0.13 }}
          data-testid="hero-altbau-bg"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background" />
      </motion.div>

      <motion.div
        style={{ y: textureY }}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.09]"
      >
        <img src={TEXTURE_URL} alt="" className="h-[120%] w-full object-cover" />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 py-16 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground md:mb-8 md:text-sm"
          data-testid="hero-kicker"
        >
          {hero.kicker}
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-heading text-[clamp(3.25rem,11vw,9.5rem)] font-black leading-[0.92] tracking-tighter text-foreground"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-primary" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 0.9,
                  delay: 0.3 + i * 0.14,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base text-muted-foreground md:text-lg"
          data-testid="hero-subline"
        >
          {hero.subline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-call-button"
            className="group inline-flex items-center gap-3 rounded-full bg-primary px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground"
          >
            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
            Projekt besprechen
          </a>
          <a
            href="#leistungen"
            data-testid="hero-services-button"
            className="inline-flex items-center gap-3 rounded-full border border-foreground/20 px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-foreground transition-colors duration-300 hover:border-foreground hover:bg-foreground hover:text-background"
          >
            Leistungen ansehen
          </a>
        </motion.div>
      </div>

      <motion.div
        style={{ y: wordY }}
        aria-hidden="true"
        className="pointer-events-none relative z-0 -mb-4 select-none overflow-hidden"
      >
        <p className="whitespace-nowrap text-center font-heading text-[19vw] font-black leading-[0.8] tracking-tighter text-foreground/[0.07]">
          RUNDUM
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between border-t border-foreground/10 px-6 py-5 md:px-10"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground md:text-xs">
          Trockenbau · Verputzen · Innenausbau · Altbau
        </p>
        <p
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground md:text-xs"
          data-testid="scroll-indicator"
        >
          Scrollen <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        </p>
      </motion.div>
    </section>
  );
};
